import { useMonyawn } from "../context/MonyawnContext";
import {
  createCheckpoint,
  createStageEvent,
  createMemo,
  nowIso,
  isClosedStage,
  stageMeta,
} from "../workflow";
import { OpportunityStatus } from "../types";

export function useOpportunityClose() {
  const { patchState, selectedOpportunity, setNotice } = useMonyawn();

  const handleCloseLost = () => {
    if (!selectedOpportunity) return;

    if (isClosedStage(selectedOpportunity.current_stage)) {
      setNotice({ tone: "info", message: "This opportunity is already at a terminal stage." });
      return;
    }

    const updatedOpportunity = {
      ...selectedOpportunity,
      current_stage: "closed_lost" as const,
      updated_at: nowIso(),
      status: "closed_lost" as OpportunityStatus,
    };

    const checkpoint = createCheckpoint(
      updatedOpportunity,
      stageMeta.closed_lost.label,
      `Close from ${stageMeta[selectedOpportunity.current_stage].label}`,
      "medium",
      "proceed_with_warning",
      "Opportunity was intentionally closed as lost from the active workflow.",
      "low",
      "low",
    );

    const memo = createMemo(
      selectedOpportunity.opportunity_id,
      "final",
      "Opportunity was closed as lost after guided review and lifecycle capture.",
      checkpoint.confidence_level,
      false,
    );

    const stageEvent = createStageEvent(
      selectedOpportunity.opportunity_id,
      "closed_lost",
      "completed",
      "user",
      new Date().toISOString(), // Fallback if state userId is missing in closure context
      `Closed as lost from ${stageMeta[selectedOpportunity.current_stage].label}.`,
    );

    patchState(
      (current) => ({
        ...current,
        opportunities: current.opportunities.map((opportunity) =>
          opportunity.opportunity_id === updatedOpportunity.opportunity_id
            ? updatedOpportunity
            : opportunity,
        ),
        checkpoints: [checkpoint, ...current.checkpoints],
        memos: [memo, ...current.memos],
        events: [stageEvent, ...current.events],
      }),
      "Opportunity closed as lost with audit history preserved.",
    );
  };

  return { handleCloseLost };
}
